import concat from 'lodash/concat';
import filter from 'lodash/filter';
import findIndex from 'lodash/findIndex';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import CaseStudy from '../models/CaseStudy/CaseStudy';

import CaseStudyCard from '../components/case-study-card';
import JsonLd from '../components/jsonld';
import Layout from '../components/layout';
import SEO from '../components/seo';
import Underline from '../components/underline';

import {
  AnimateIn,
  Button,
  Container,
  Grid,
  GridItem,
  Heading,
  Label,
  Select,
  Subtitle,
  Text,
  colors
} from '../ui-components';

import { extractGlobalNodes } from '../utils/cross-space-reference-helper.jsx';
const {dedupeLocalizedNodeList, dedupeGeoSpecificIndustriesBySpaceId} = require('../utils/contentful-dedupe.jsx');

import { usePlaceholderImage } from '../query-hooks/use-placeholder-image';

/**
 * Initializer function used to set the industry or tool type based on
 * a query string passed in from previous page. Defaults to "All"
 * if the industry or tool property is not provided
 *
 * @function initializeType
 * 
 * @param {object} location url location object
 * @param {string} type tool or industry
 * 
 * @returns {string}
 */
const initializeType = (location, type) => {
  let parsed = queryString.parse(location.search);

  if (type === 'tool') return parsed.tool ? parsed.tool : 'all';
  if (type === 'industry') return parsed.industry ? parsed.industry : 'all';
}

const getLocalCaseStudiesMain = (location, data, content, common, localSEOSlug, spaceId = process.env.CONTENTFUL_GLOBAL_SPACE_ID) => {
  const placeholderImage = usePlaceholderImage()?.childImageSharp?.gatsbyImageData;
  const caseStudies = dedupeLocalizedNodeList(data.allContentfulCaseStudy.nodes, spaceId).map(node => {
    
    let matchedIndustries = [];
    let matchedServiceLines = [];

    if (spaceId !== process.env.CONTENTFUL_GLOBAL_SPACE_ID) {
      const industries = node.industries;
      const serviceLines = node.serviceLines;

      const globalIndustries = data.allContentfulIndustry;
      const globalServiceLines = data.allContentfulServiceLine;
 
      matchedIndustries = extractGlobalNodes(industries, globalIndustries);
      matchedServiceLines = extractGlobalNodes(serviceLines, globalServiceLines);  
    }
  
    let caseStudy = new CaseStudy(node);
    
    caseStudy.relatedIndustries = caseStudy.relatedIndustries ? caseStudy.relatedIndustries.concat(matchedIndustries) : [].concat(matchedIndustries);
    caseStudy.relatedServiceLines = caseStudy.relatedServiceLines ? caseStudy.relatedServiceLines.concat(matchedServiceLines) : [].concat(matchedServiceLines);

    return caseStudy;
  });

  const globalCaseStudies = data.allContentfulCaseStudy;

  let globalFeaturedCaseStudies = data.featuredCaseStudies.nodes[0].globalFeaturedCaseStudies || [];

  let matchedCaseStudyNodes = extractGlobalNodes(globalFeaturedCaseStudies, globalCaseStudies);
  let matchedCaseStudies = matchedCaseStudyNodes.map(caseStudy => new CaseStudy(caseStudy));

  let featuredCaseStudies = data.featuredCaseStudies.nodes[0].featuredCaseStudies || [];

  featuredCaseStudies = featuredCaseStudies.map(edge => new CaseStudy(edge));
  featuredCaseStudies = featuredCaseStudies.concat(matchedCaseStudies);

  let industries = dedupeLocalizedNodeList(data.allContentfulIndustry.nodes, spaceId);
  industries = dedupeGeoSpecificIndustriesBySpaceId(industries, spaceId).map(node => node.title).sort((a, b) => a.localeCompare(b));

  let tools = data.allContentfulCaseStudy.nodes;

  const showMoreIncrement = 6;
  const initialDisplayCount =
    typeof window !== 'undefined' ? window.history.state?.displayCount ?? showMoreIncrement : showMoreIncrement;
  const [displayCount, setDisplayCount] = useState(initialDisplayCount);
  const [industryType, setIndustryType] = useState(initializeType(location, 'industry'));
  const [serviceLineType, setServiceLineType] = useState('all');
  const [toolType, setToolType] = useState(initializeType(location, 'tool'));
  const [list, setList] = useState([]);
  const seoSlug = localSEOSlug;

  /**
   * Helper function used to update the current history with the most recent display count. This ensures
   * the display count is preserved when the user navigates to a blog, and then returns to the list expecting
   * their display to be the same as when they left
   *
   * @function setURLPageCount
   * 
   * @param {number} count the count of case studies currently displayed
   */
  const setURLPageCount = count => {
    if (typeof window !== 'undefined') {
      let parsed = queryString.parse(location.search);
      parsed = { ...parsed, displayCount: count };
      let pageCountString = queryString.stringify(parsed); 
      pageCountString = pageCountString.replace(/%20/g, "+");

      window.history.replaceState({ ...window.history.state, displayCount: count }, 'Case Studies', `?${pageCountString}`);
    }
  };

  /**
   * Lifecycle method used to set the case studies based on the filter options selected. This ensures
   * that the case studies represented always reflects what filters have been chosen. The complete list
   * is filtered to display FEATURED case studies first, followed by remaining case studies - The logic
   * ensures that the union of FEATURED + ALL case studies does not contain duplicates
   *
   * @function useEffect
   */
  useEffect(() => {
    const caseStudiesNoFeatures = filter(caseStudies, study => {
      return findIndex(featuredCaseStudies, { slug: study.slug }) < 0;
    });
    const caseStudiesFeaturesFirst = concat(featuredCaseStudies, caseStudiesNoFeatures);
  
    const filteredCaseStudies = caseStudiesFeaturesFirst.filter(caseStudy => {
      const relatedIndustries = caseStudy.relatedIndustries
        ? caseStudy.relatedIndustries.map(industry => industry.title)
        : [];
      const relatedServiceLines = caseStudy.relatedServiceLines
        ? caseStudy.relatedServiceLines.map(serviceLine => serviceLine.title)
        : [];
      const relatedTools = caseStudy.tools
        ? caseStudy.tools.map(tool => tool)
        : [];
  
      const matchesIndustry = industryType === 'all' || relatedIndustries.includes(industryType);
      const matchesServiceLine = serviceLineType === 'all' || relatedServiceLines.includes(serviceLineType);
      const matchesTool = toolType === 'all' || relatedTools.includes(toolType);
  
      return matchesIndustry && matchesServiceLine && matchesTool;
    });
  
    // Set the filtered list
    setList(filteredCaseStudies);
  
    // Reset page count and display count
    setURLPageCount(initialDisplayCount);
    setDisplayCount(initialDisplayCount);
  }, [serviceLineType, industryType, toolType]);
  
  /**
   * Click handler for the show more button that updates the current display count in both local state,
   * history state
   *
   * @function showMoreHandler
   */
  const showMoreHandler = () => {
    let newDisplayCount = displayCount + showMoreIncrement;
    newDisplayCount = newDisplayCount < caseStudies.length ? newDisplayCount : caseStudies.length;
  
    setURLPageCount(newDisplayCount);
    setDisplayCount(newDisplayCount);
  };

  /**
   * Renders the SEO meta-tag and structured data elements
   *
   * @function renderSEO
   * @returns {JSX}
   */
  const renderSEO = () => {
    const image = placeholderImage;

    return (
      <>
        <SEO title={content.SEO_TITLE} description={content.SEO_DESCRIPTION} slug={seoSlug} image={image?.images?.fallback?.src} />
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: content.SEO_TITLE,
            alternateName: content.TITLE,
            description: content.SEO_DESCRIPTION,
            abstract: content.SEO_DESCRIPTION,
            headline: content.TITLE,
            inLanguage: content.IN_LANGUAGE,
            url: `http://credera.com${location.pathname}`,
            isAccessibleForFree: true,
            image: image?.images?.fallback?.src
          }}
        />
      </>
    );
  };

  /**
   * Renders the title of the page
   *
   * @function renderTitleSection
   * @returns {JSX}
   */
  const renderTitleSection = () => {
    return (
      <TitleContainer>
        <PageCategoryText size={'large'}>{content.LABEL}</PageCategoryText>
        <TitleHeading level={1}>
          <Underline phrase={content.TITLE} underlinedText={content.TITLE__UNDERLINE} />
        </TitleHeading>
        <PageSubtitle size="large">{content.SUBTITLE}</PageSubtitle>
      </TitleContainer>
    );
  };

  /**
   * Returns the JSX for the set of Industry filters
   *
   * @function renderIndustryTypes
   * 
   * @returns {JSX}
   */
  const renderIndustryTypes = () => {
    const options = industries.map(type => (
      <option key={type} value={type}>
        {type}
      </option>
    ));

    let defaultText = 'All Industries';

    return (
      <DropDown onChange={e => setIndustryType(e.target.value)} value={industryType}>
        <option value="" defaultValue hidden>
          {defaultText}
        </option>
        <option value="all">{defaultText}</option>
        {options}
      </DropDown>
    );
  };

  /**
   * Returns the array of unique service lines
   *
   * @function getApplicableServiceLines
   * 
   * @returns {serviceLines} the list of unique service lines
   */
  const getApplicableServiceLines = (spaceId) => {
    const caseStudyList = dedupeLocalizedNodeList(data.allContentfulCaseStudy.nodes, spaceId);
    const serviceLines = [];

    caseStudyList.forEach(study => {
      study.offerings?.forEach(offering => {
        if(!serviceLines.includes(offering.title)) {
          serviceLines.push(offering.title);
        }
      })
    })
    return serviceLines.sort((a, b) => a.localeCompare(b));
  }

  /**
   * Returns the JSX for the set of Service Line filters
   *
   * @function renderServiceLineTypes
   * 
   * @returns {JSX}
   */
  const renderServiceLineTypes = (spaceId) => {
    let serviceLines = getApplicableServiceLines(spaceId)
    const options = serviceLines.map(type => (
      <option key={type} value={type}>
        {type}
      </option>
    ));

    let defaultText = 'All Offerings';

    return (
      <DropDown onChange={e => setServiceLineType(e.target.value)}>
        <option value="" defaultValue hidden>
          {defaultText}
        </option>
        <option value="all">{defaultText}</option>
        {options}
      </DropDown>
    );
  };

  /**
   * Returns the array of unique tools
   *
   * @function getApplicableTools
   * 
   * @returns {tools} the list of unique tools
   */
  const getApplicableTools = () => {
    const toolList = [];

    tools.forEach(study => {
      study.tools?.forEach(tool => {
        if(!toolList.includes(tool)) {
          toolList.push(tool);
        }
      })
    })
    return toolList.sort((a, b) => a.localeCompare(b));
  }

  /**
   * Returns the JSX for the set of tool filters
   *
   * @function renderToolTypes
   * 
   * @returns {JSX}
   */
  const renderToolTypes = () => {
    let tools = getApplicableTools();

    const options = tools?.map(tool => (
      <option key={tool} value={tool}>
        {tool}
      </option>
    ));

    let defaultText = 'All Tools';

    return (
      <DropDown onChange={e => setToolType(e.target.value)} value={toolType}>
        <option value="" defaultValue hidden>
          {defaultText}
        </option>
        <option value="all">{defaultText}</option>
        {options}
      </DropDown>
    );
  };

  /**
   * Returns the JSX for the filters section
   *
   * @function renderFiltersSection
   * 
   * @returns {JSX}
   */
  const renderFiltersSection = (spaceId) => {
    return (
      <Grid noWrap>
        <GridItem col={6} collg={12}>
          <CSHeading level={5}>{`Case studies`}</CSHeading>
        </GridItem>
        <GridItem col={6} collg={12}>
          <Grid noWrap justifyContent="flex-end">
            <GridItem col={7} colsm={12}>
              {renderIndustryTypes()}
            </GridItem>
            <GridItem col={5} colsm={12}>
              {renderServiceLineTypes(spaceId)}
            </GridItem>
            <GridItem col={5} colsm={12}>
              {renderToolTypes()}
            </GridItem>
          </Grid>
        </GridItem>
      </Grid>
    );
  };

  /**
   * Returns the JSX for the show more button
   *
   * @function renderShowMore
   * 
   * @returns {JSX} show more button
   */
  const renderShowMore = () => {
    const showMoreButton = (
      <ShowMoreWrapper>
        <Button buttonstyle={'secondary'} onClick={showMoreHandler}>
          {common.SHOW_MORE__BUTTON}
        </Button>
      </ShowMoreWrapper>
    );
    return list.length > displayCount ? showMoreButton : null;
  };

  /**
   * Returns the JSX for the list of non-featured case studies
   *
   * @function renderCaseStudies
   * 
   * @returns {JSX} list of non-featured case studies
   */
  const renderCaseStudies = () => {
    if (list.length === 0) {
      return (
        <EmptyResultsWrapper>
          <Heading level={6}>
            {content.NO_RESULTS__TITLE}
          </Heading>
          <Text>{content.NO_RESULTS__TEXT}</Text>
        </EmptyResultsWrapper>
      );
    }

    return list.slice(0, displayCount).map(caseStudy => (
      <GridItem key={caseStudy.slug} col={6} collg={12}>
        <CaseStudyCard caseStudy={caseStudy} />
      </GridItem>
    ));
  };

  return (
    <Layout>
      {renderSEO()}
      <Background>
        <Container>
          <AnimateIn onScroll>{renderTitleSection()}</AnimateIn>
          <AnimateIn onScroll direction={'none'} delay={600}>
            {renderFiltersSection(spaceId)}
          </AnimateIn>
          <AnimateIn onScroll direction={'none'} delay={600}>
            <CaseStudiesGrid gridGap={40}>{renderCaseStudies()}</CaseStudiesGrid>
            {renderShowMore()}
          </AnimateIn>
        </Container>
      </Background>
    </Layout>
  );
};


const Background = styled.div`
  background-color: ${colors.sandBeige};
`;

const TitleContainer = styled(Container)`
  text-align: center;
  padding-bottom: 80px;
`;

const PageCategoryText = styled(props => <Label {...props} />)`
  margin-bottom: 11px;
`;

const TitleHeading = styled(Heading)`
  margin-top: 0px;
`;

const CSHeading = styled(Heading)`
  line-height: 42px;
`;

const PageSubtitle = styled(Subtitle)`
  margin-top: 30px;
`;

const CaseStudiesGrid = styled(Grid)`
  margin-top: 40px;
`;

const ShowMoreWrapper = styled.div`
  text-align: center;
  margin-bottom: 80px;
  margin-top: 40px;
`;

const EmptyResultsWrapper = styled.div`
  width: 100%;
  padding: 15% 0;
  text-align: center;
`;

const DropDown = styled(Select)`
  padding-right: 45px;
  overflow: hidden;
  text-overflow: ellipsis;
`

export default getLocalCaseStudiesMain;
